import React, { useState, useMemo } from 'react';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Calculator, Copy, RotateCcw, Lock, Crown, Sparkles, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const CURRENCY_META = [
  { code: 'USD', symbol: '$' },
  { code: 'CAD', symbol: 'CA$' },
  { code: 'AUD', symbol: 'A$' },
  { code: 'BRL', symbol: 'R$' },
  { code: 'EUR', symbol: '€' },
  { code: 'GBP', symbol: '£' },
  { code: 'MXN', symbol: 'MX$' },
  { code: 'ARS', symbol: 'AR$' },
  { code: 'CLP', symbol: 'CL$' },
  { code: 'COP', symbol: 'CO$' },
  { code: 'PEN', symbol: 'S/' }
];

const PRESET_KEYS = [
  { key: 'hobby', value: 0.25 },
  { key: 'small_biz', value: 0.50 },
  { key: 'farm', value: 1.00 }
];

const EMPTY = {
  rollPrice: '', rollWeight: '1000', partWeight: '',
  printHours: '', printMinutes: '', printerHourCost: '',
  extras: '',
  laborMinutes: '', laborHourValue: '',
  profitMargin: '50'
};

const num = (v) => {
  const n = parseFloat(String(v).replace(',', '.'));
  return isNaN(n) ? 0 : n;
};

const LOCALE_BY_LANG = { pt: 'pt-BR', en: 'en-US', es: 'es-ES' };

export function PriceCalculatorPage() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const isPremium = user?.is_premium;
  const [currency, setCurrency] = useState('BRL');
  const [data, setData] = useState(EMPTY);

  const cur = CURRENCY_META.find((c) => c.code === currency) || CURRENCY_META[0];
  const lang = (i18n.resolvedLanguage || i18n.language || 'pt').slice(0, 2);
  const locale = LOCALE_BY_LANG[lang] || 'pt-BR';
  const fmt = (v) => `${cur.symbol} ${v.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const set = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const results = useMemo(() => {
    const rollPrice = num(data.rollPrice);
    const rollWeight = num(data.rollWeight) || 1;
    const partWeight = num(data.partWeight);
    const materialCost = (rollPrice / rollWeight) * partWeight;

    const printTotal = num(data.printHours) + (num(data.printMinutes) / 60);
    const printCost = printTotal * num(data.printerHourCost);

    const extrasCost = num(data.extras);

    const laborHours = num(data.laborMinutes) / 60;
    const laborCost = laborHours * num(data.laborHourValue);

    const totalCost = materialCost + printCost + extrasCost + laborCost;

    const marginPct = Math.min(Math.max(num(data.profitMargin), 0), 99);
    const marginDecimal = marginPct / 100;
    const recommended = marginDecimal < 1 ? totalCost / (1 - marginDecimal) : totalCost;
    const profit = recommended - totalCost;

    return { materialCost, printCost, extrasCost, laborCost, totalCost, recommended, profit };
  }, [data]);

  const handleCopy = async () => {
    const text = `${t('calculator.summary.copy_text_header')}
${t('calculator.summary.material')}: ${fmt(results.materialCost)}
${t('calculator.summary.printing')}: ${fmt(results.printCost)}
${t('calculator.summary.extras')}: ${fmt(results.extrasCost)}
${t('calculator.summary.labor')}: ${fmt(results.laborCost)}
${t('calculator.summary.total_cost')}: ${fmt(results.totalCost)}
${t('calculator.summary.recommended')}: ${fmt(results.recommended)}
${t('calculator.summary.profit')}: ${fmt(results.profit)}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('calculator.summary.copy_success'));
    } catch {
      toast.error(t('calculator.summary.copy_error'));
    }
  };

  const handleReset = () => {
    setData(EMPTY);
    toast.info(t('calculator.summary.reset_info'));
  };

  const showPremiumCTA = () => toast.info(t('common.premium_soon_toast'), { duration: 4000 });

  const benefits = t('calculator.premium_gate.benefits', { returnObjects: true });

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      <main data-testid="calculator-page" className="mt-16 pt-12 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-3">
              <div className="flex items-center space-x-3">
                <Calculator className="w-8 h-8 text-[#00FF99]" />
                <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight">{t('calculator.title')}</h1>
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-zinc-400 text-sm font-semibold">{t('calculator.currency_label')}</label>
                <select
                  data-testid="currency-select"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="bg-[#1A1A1A] border border-[#27272A] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-[#0066FF] transition-colors"
                >
                  {CURRENCY_META.map((c) => (
                    <option key={c.code} value={c.code}>{c.code} — {t(`calculator.currencies.${c.code}`)}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-zinc-400 max-w-3xl">
              {t('calculator.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Section title={t('calculator.sections.material')} testid="section-material">
                <Row>
                  <Field label={t('calculator.fields.roll_price')} prefix={cur.symbol}>
                    <Input value={data.rollPrice} onChange={(v) => set('rollPrice', v)} placeholder="0,00" testid="input-roll-price" />
                  </Field>
                  <Field label={t('calculator.fields.roll_weight')}>
                    <Input value={data.rollWeight} onChange={(v) => set('rollWeight', v)} placeholder="1000" testid="input-roll-weight" />
                  </Field>
                </Row>
                <Field label={t('calculator.fields.part_weight')}>
                  <Input value={data.partWeight} onChange={(v) => set('partWeight', v)} placeholder="0" testid="input-part-weight" />
                </Field>
              </Section>

              <Section title={t('calculator.sections.printing')} testid="section-printing">
                <Row>
                  <Field label={t('calculator.fields.hours')}>
                    <Input value={data.printHours} onChange={(v) => set('printHours', v)} placeholder="0" testid="input-print-hours" />
                  </Field>
                  <Field label={t('calculator.fields.minutes')}>
                    <Input value={data.printMinutes} onChange={(v) => set('printMinutes', v)} placeholder="0" testid="input-print-minutes" />
                  </Field>
                </Row>
                <Field label={t('calculator.fields.printer_cost')} prefix={cur.symbol}>
                  <Input value={data.printerHourCost} onChange={(v) => set('printerHourCost', v)} placeholder="0,00" testid="input-printer-cost" />
                </Field>
                <div className="flex flex-wrap gap-2 pt-1">
                  {PRESET_KEYS.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      data-testid={`preset-${p.key}`}
                      onClick={() => set('printerHourCost', String(p.value))}
                      className="text-xs font-semibold bg-[#1A1A1A] border border-[#27272A] hover:border-[#0066FF] hover:text-[#0066FF] text-zinc-300 px-3 py-1.5 rounded-full transition-colors"
                    >
                      {t(`calculator.presets.${p.key}`)} · {cur.symbol} {p.value.toFixed(2)}/h
                    </button>
                  ))}
                </div>
              </Section>

              <Section title={t('calculator.sections.extras')} testid="section-extras">
                <Field label={t('calculator.fields.extras')} prefix={cur.symbol} hint={t('calculator.fields.extras_hint')}>
                  <Input value={data.extras} onChange={(v) => set('extras', v)} placeholder="0,00" testid="input-extras" />
                </Field>
              </Section>

              <Section title={t('calculator.sections.labor')} testid="section-labor">
                <Row>
                  <Field label={t('calculator.fields.labor_minutes')}>
                    <Input value={data.laborMinutes} onChange={(v) => set('laborMinutes', v)} placeholder="0" testid="input-labor-minutes" />
                  </Field>
                  <Field label={t('calculator.fields.labor_hour')} prefix={cur.symbol}>
                    <Input value={data.laborHourValue} onChange={(v) => set('laborHourValue', v)} placeholder="0,00" testid="input-labor-hour" />
                  </Field>
                </Row>
              </Section>

              <Section title={t('calculator.sections.profit')} testid="section-profit">
                <Field label={t('calculator.fields.margin')} suffix="%" hint={t('calculator.fields.margin_hint')}>
                  <Input value={data.profitMargin} onChange={(v) => set('profitMargin', v)} placeholder="50" testid="input-margin" />
                </Field>
              </Section>
            </div>

            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24">
                <div className="relative">
                  <div className={`backdrop-blur-xl bg-gradient-to-br from-[#121212]/80 to-[#0A0A0A]/80 border border-white/10 rounded-2xl p-6 ${!isPremium ? 'overflow-hidden' : ''}`}>
                    <h3 className="text-2xl font-black text-white tracking-tight mb-5 flex items-center space-x-2">
                      <Sparkles className="w-6 h-6 text-[#00FF99]" />
                      <span>{t('calculator.summary.title')}</span>
                    </h3>

                    <div className={!isPremium ? 'filter blur-md select-none pointer-events-none' : ''}>
                      <ResultLine label={t('calculator.summary.material')} value={fmt(results.materialCost)} />
                      <ResultLine label={t('calculator.summary.printing')} value={fmt(results.printCost)} />
                      <ResultLine label={t('calculator.summary.extras')} value={fmt(results.extrasCost)} />
                      <ResultLine label={t('calculator.summary.labor')} value={fmt(results.laborCost)} />
                      <div className="border-t border-white/10 my-3" />
                      <ResultLine label={t('calculator.summary.total_cost')} value={fmt(results.totalCost)} bold />
                      <ResultLine label={t('calculator.summary.profit')} value={fmt(results.profit)} accent="green" />
                      <div className="bg-gradient-to-r from-[#0066FF]/20 to-[#00FF99]/20 border border-[#00FF99]/40 rounded-xl p-4 mt-4">
                        <p className="text-xs text-zinc-300 font-bold uppercase tracking-wider mb-1">{t('calculator.summary.recommended')}</p>
                        <p data-testid="recommended-price" className="text-3xl font-black text-[#00FF99]">
                          {fmt(results.recommended)}
                        </p>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-2 mt-5">
                        <button
                          data-testid="copy-button"
                          onClick={handleCopy}
                          className="flex-1 inline-flex items-center justify-center space-x-2 bg-[#0066FF] text-white hover:bg-[#3385FF] transition-colors font-bold rounded-lg px-4 py-2.5 text-sm"
                        >
                          <Copy className="w-4 h-4" />
                          <span>{t('calculator.summary.copy')}</span>
                        </button>
                        <button
                          data-testid="reset-button"
                          onClick={handleReset}
                          className="flex-1 inline-flex items-center justify-center space-x-2 bg-[#1A1A1A] border border-[#27272A] text-white hover:border-zinc-500 transition-colors font-semibold rounded-lg px-4 py-2.5 text-sm"
                        >
                          <RotateCcw className="w-4 h-4" />
                          <span>{t('calculator.summary.reset')}</span>
                        </button>
                      </div>
                    </div>

                    {!isPremium && (
                      <div data-testid="premium-overlay" className="absolute inset-0 flex flex-col justify-center items-center p-6 text-center">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#0066FF] to-[#00FF99] flex items-center justify-center mb-4 shadow-[0_0_30px_rgba(0,255,153,0.4)]">
                          <Lock className="w-7 h-7 text-black" />
                        </div>
                        <h4 className="text-xl font-black text-white tracking-tight mb-2">
                          {t('calculator.premium_gate.title')}
                        </h4>
                        <p className="text-sm text-zinc-300 mb-5 max-w-xs">
                          {t('calculator.premium_gate.subtitle')}
                        </p>
                        <ul className="text-left text-xs text-zinc-300 space-y-1.5 mb-6 max-w-xs w-full">
                          {Array.isArray(benefits) && benefits.map((b) => (
                            <li key={b} className="flex items-start space-x-2">
                              <CheckCircle2 className="w-3.5 h-3.5 text-[#00FF99] mt-0.5 shrink-0" />
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                        <button
                          data-testid="subscribe-premium-button"
                          onClick={showPremiumCTA}
                          className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#0066FF] to-[#00FF99] text-black hover:opacity-90 transition-all duration-300 font-bold rounded-lg px-5 py-2.5 shadow-[0_0_20px_rgba(0,255,153,0.4)] hover:shadow-[0_0_30px_rgba(0,255,153,0.6)]"
                        >
                          <Crown className="w-4 h-4" />
                          <span>{t('calculator.premium_gate.cta')}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="text-zinc-500 text-xs text-center mt-12 max-w-3xl mx-auto leading-relaxed">
            {t('calculator.footer_note')}
          </p>
        </div>
      </main>
    </div>
  );
}

const inputClass = "w-full bg-[#1A1A1A] border border-[#27272A] rounded-lg px-4 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-[#0066FF] transition-colors";

function Section({ title, testid, children }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={testid}
      className="backdrop-blur-xl bg-[#121212]/60 border border-white/10 rounded-2xl p-6 space-y-4"
    >
      <h2 className="text-xl font-bold text-white tracking-tight">{title}</h2>
      {children}
    </motion.section>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, hint, prefix, suffix, children }) {
  const child = React.Children.only(children);
  return (
    <div>
      <label className="block text-sm font-semibold text-white mb-1.5">{label}</label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm font-semibold pointer-events-none">{prefix}</span>
        )}
        {React.cloneElement(child, {
          className: `${inputClass} ${prefix ? 'pl-12' : ''} ${suffix ? 'pr-10' : ''}`
        })}
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm font-semibold pointer-events-none">{suffix}</span>
        )}
      </div>
      {hint && <p className="text-xs text-zinc-500 mt-1">{hint}</p>}
    </div>
  );
}

function Input({ value, onChange, placeholder, testid, className }) {
  return (
    <input
      type="text"
      inputMode="decimal"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/[^0-9.,]/g, ''))}
      placeholder={placeholder}
      data-testid={testid}
      className={className}
    />
  );
}

function ResultLine({ label, value, bold, accent }) {
  const accentClass = accent === 'green' ? 'text-[#00FF99]' : 'text-white';
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className={`text-sm ${bold ? 'text-white font-bold' : 'text-zinc-400'}`}>{label}</span>
      <span className={`text-sm font-mono ${bold ? 'font-bold' : ''} ${accentClass}`}>{value}</span>
    </div>
  );
}
